/**
 * ListPreviewDialog — Preview premium e seguro de uma lista de contatos.
 *
 * Usa ImportPreviewShell + componentes compartilhados (MetricCard, VariableChip,
 * StatusDot). Dados via RPC `get_campaign_list_preview` (limite 50, mascaramento
 * server-side de telefone e e-mail).
 *
 * RELEASE:
 *   2026-05-17 v3.0.0 - Refatorado para usar ImportPreviewShell compartilhado
 *                       com Negócios e Afiliados (sem mudança visual).
 *   2026-05-17 v2.0.0 - Refatoração para RPC server-side, mascaramento e
 *                       leiaute premium alinhado ao padrão do sistema.
 */
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FileSpreadsheet,
  Users,
  Variable,
  Inbox,
  CheckCircle2,
  RefreshCw,
  XCircle,
} from 'lucide-react';

import { useCampaignListPreview } from '@/hooks/useCampaignListPreview';
import { toast } from '@/lib/toast';
import { ImportPreviewShell } from '@/components/imports/shared/ImportPreviewShell';
import { ImportPreviewMetricCard } from '@/components/imports/shared/ImportPreviewMetricCard';
import { ImportPreviewVariableChip } from '@/components/imports/shared/ImportPreviewVariableChip';
import { ImportPreviewStatusDot } from '@/components/imports/shared/ImportPreviewStatusDot';

interface CampaignList {
  id: string;
  name: string;
  hash_code: string;
  total_rows: number;
  imported_contacts: number;
  updated_contacts: number;
  skipped_contacts: number;
  columns: string[];
  variable_columns: string[];
  status: string;
  expires_at: string | null;
  created_at: string;
}

interface ListPreviewDialogProps {
  list: CampaignList;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_DOT: Record<string, string> = {
  imported: 'bg-emerald-500',
  updated: 'bg-blue-500',
  skipped: 'bg-amber-500',
  error: 'bg-red-500',
};

const STATUS_LABELS: Record<string, string> = {
  imported: 'Importado',
  updated: 'Atualizado',
  skipped: 'Ignorado',
  error: 'Erro',
};

export default function ListPreviewDialog({
  list,
  open,
  onOpenChange,
}: ListPreviewDialogProps) {
  const { data: items, isLoading } = useCampaignListPreview(list.id, open);

  // Variáveis a mostrar na tabela (até 3 colunas)
  const variablePreviewCols = useMemo(
    () => list.variable_columns.slice(0, 3),
    [list.variable_columns],
  );

  const copyVariable = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success(`Copiado: ${token}`);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  return (
    <ImportPreviewShell
      open={open}
      onOpenChange={onOpenChange}
      headerIcon={<FileSpreadsheet className="h-4 w-4" />}
      title={list.name}
      hashCode={list.hash_code}
      createdAt={list.created_at}
      metrics={
        <>
          <ImportPreviewMetricCard
            title="Total"
            value={list.total_rows}
            icon={<Users className="h-4 w-4" />}
            variant="primary"
          />
          <ImportPreviewMetricCard
            title="Importados"
            value={list.imported_contacts}
            icon={<CheckCircle2 className="h-4 w-4" />}
            variant="success"
          />
          <ImportPreviewMetricCard
            title="Atualizados"
            value={list.updated_contacts}
            icon={<RefreshCw className="h-4 w-4" />}
            variant="info"
          />
          <ImportPreviewMetricCard
            title="Ignorados"
            value={list.skipped_contacts}
            icon={<XCircle className="h-4 w-4" />}
            variant="purple"
          />
        </>
      }
      variablesBlock={
        <div className="rounded-lg border bg-gradient-to-br from-muted/40 to-muted/10 p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Variable className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold leading-tight">
                Variáveis disponíveis
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Clique para copiar. Use estes tokens em mensagens, templates e fluxos.
              </p>
            </div>
          </div>

          <div className="space-y-2.5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium mb-1.5">
                Padrão
              </p>
              <div className="flex flex-wrap gap-1.5">
                <ImportPreviewVariableChip
                  token="{{nome}}"
                  variant="primary"
                  onCopy={copyVariable}
                />
                <ImportPreviewVariableChip
                  token="{{telefone}}"
                  variant="primary"
                  onCopy={copyVariable}
                />
              </div>
            </div>

            {list.variable_columns.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium mb-1.5">
                  Personalizadas ({list.variable_columns.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {list.variable_columns.map((col) => (
                    <ImportPreviewVariableChip
                      key={col}
                      token={`{{${col}}}`}
                      variant="outline"
                      onCopy={copyVariable}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Amostra de contatos</h3>

        {isLoading ? (
          <div className="rounded-md border overflow-hidden">
            <div className="space-y-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-none" />
              ))}
            </div>
          </div>
        ) : !items || items.length === 0 ? (
          <div className="rounded-md border border-dashed py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-sm">Nenhum contato disponível para preview.</p>
          </div>
        ) : (
          <TooltipProvider delayDuration={150}>
            <div className="rounded-md border overflow-hidden">
              <div className="max-h-[420px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-9 w-10 text-xs">#</TableHead>
                      <TableHead className="h-9 text-xs">Nome</TableHead>
                      <TableHead className="h-9 text-xs">Telefone</TableHead>
                      <TableHead className="h-9 text-xs">E-mail</TableHead>
                      {variablePreviewCols.map((col) => (
                        <TableHead key={col} className="h-9 text-xs">
                          {col}
                        </TableHead>
                      ))}
                      <TableHead className="h-9 text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={item.id} className="text-xs">
                        <TableCell className="py-2 text-muted-foreground tabular-nums">
                          {index + 1}
                        </TableCell>
                        <TableCell className="py-2 font-medium max-w-[180px] truncate">
                          {item.name || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                          {item.phone_masked || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[180px] truncate">
                          {item.email_masked || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        {variablePreviewCols.map((col) => {
                          const v = item.variables[col];
                          return (
                            <TableCell
                              key={col}
                              className="py-2 max-w-[160px] truncate"
                            >
                              {v ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="truncate block">{v}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>{v}</TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-muted-foreground/60">—</span>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="py-2">
                          <ImportPreviewStatusDot
                            colorClass={STATUS_DOT[item.status] ?? 'bg-muted-foreground'}
                            label={STATUS_LABELS[item.status] ?? item.status}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>
    </ImportPreviewShell>
  );
}
